import axios from "@/lib/axios";
import { Policy } from "@/modules/groups/interfaces/policy";
import { useQuery } from "@tanstack/vue-query";
import { useCompanyGroupPoliciesStore } from "@/stores/companyGroupPolicies";
import { storeToRefs } from "pinia";
import { MaybeRef, isRef, onMounted, watch } from "vue";

interface Props {
  params: Params;
  immediate?: boolean;
}

interface Params {
  groupId?: MaybeRef<number | undefined>;
  filters?: MaybeRef<{ [key: string]: string | number | boolean | undefined }>;
}

export const getCompanyGroupPolicies = async (params: Params): Promise<Policy[]> => {
  const { groupId, filters } = params || {};
  const companyGroupId = isRef(groupId) ? groupId.value : groupId;
  if (!companyGroupId) return [];

  const { data } = await axios.get<Policy[]>(
    `/api/groups/${companyGroupId}/policies`,
    {
      params: { ...(isRef(filters) ? filters.value : filters) },
    },
  );
  return data;
};

const useCompanyGroupPolicies = (props: Props) => {
  const { immediate = false, params } = props || {};
  const {
    setCompanyGroupPolicies,
    getCompanyGroupPolicy,
    resetState,
    setService,
  } = useCompanyGroupPoliciesStore();
  const { groupPolicies, service } = storeToRefs(
    useCompanyGroupPoliciesStore(),
  );

  onMounted(() => {
    resetState();
  });

  const { data, isError, isLoading, refetch, isFetching } = useQuery(
    ["policies"],
    () =>
      getCompanyGroupPolicies({
        ...params,
        filters: {
          service: service.value > 0 ? service.value : undefined,
          ...params?.filters,
        },
      }),
    {
      enabled: immediate,
      structuralSharing: false,
    },
  );

  const searchPolicies = async () => {
    if (immediate || service.value > 0) {
      await refetch();
    } else {
      resetState();
    }
  };

  watch(service, () => {
    searchPolicies();
  });

  watch(data, (value) => {
    if (value) {
      setCompanyGroupPolicies(value);
    }
  });

  return {
    groupPolicies,
    isError,
    isLoading,
    isFetching,
    refetch,
    resetState,
    getCompanyGroupPolicy,
    setService,
  };
};

export default useCompanyGroupPolicies;
